import { sanitizeContent } from '~shared/utils/parser';
import { API_PREFIX, http } from './common';

export interface IFunctionVariable {
  name: string;
  type: string;
}

export interface IGetFunctionContentParsedResult
  extends Omit<IGetFunctionContentResult, 'parameters'> {
  content: string;
  params: IFunctionVariable[];
  returns?: IFunctionVariable;
}

export interface IGetFunctionContentResult extends IGetFunctionContentRequest {
  content: string;
  parameters: string;
  filePath: string;
  startingLine?: number;
  endingLine?: number;
}

export interface IGetFunctionContentRequest {
  functionName: string;
  className: string;
}

function getParamString(str: string) {
  const paramString = new RegExp(/^\((.+)(?=\))/gi).exec(str);

  if (paramString !== null) {
    return paramString[1];
  }

  return '';
}

function getResultString(str: string): string {
  const resultString = new RegExp(/(->)(.*)/gi).exec(str);

  if (resultString !== null) {
    return resultString[0].replace('->', '');
  }

  const exec = /:([^)]*)$/.exec(str);

  if (exec !== null) {
    return `result : ${exec[1].trim()}`;
  }

  return '';
}

function getFunctionSignature(dec: string) {
  const params = getParamString(dec)
    .split(/[;,]/g)
    .filter(Boolean)
    .map((param) => {
      const name = param.split(':')[0].trim();
      const type = param.split(':')[1].trim();

      return {
        name,
        type,
      };
    });

  const resultString = getResultString(dec);

  const result = resultString
    ? {
        name: resultString.split(':')[0].trim(),
        type: resultString.split(':')[1].trim(),
      }
    : {
        name: 'result',
        type: 'Variable',
      };

  return {
    params,
    result,
  };
}

export async function getFunctionContent(
  path: string,
  className: string,
): Promise<IGetFunctionContentParsedResult> {
  const chunks = path.split('.');
  const functionName = chunks.pop() as string;

  try {
    const result = await http<IGetFunctionContentResult>(API_PREFIX, {
      method: 'POST',
      body: JSON.stringify({
        verb: 'getFunctionContent',
        className,
        functionName,
      }),
      headers: new Headers({
        'Content-Type': 'application/json',
      }),
    });

    result.content = sanitizeContent(result.content);

    const { params, result: returns } = getFunctionSignature(result.parameters);

    return {
      ...result,
      params,
      returns,
    };
  } catch (err) {
    console.error(err);

    return {
      className,
      functionName,
      filePath: path,
      content: '',
      params: [],
    };
  }
}
